import { makeAutoObservable } from 'mobx';

import type { Main } from '@app/store';

import { DURATION_SECONDS, STORAGE_KEY } from '../constants';
import type { Cooldowns } from '../types';
import { OtpType } from '../types';

export class OtpCooldownStore {
  private readonly mainStore: Main;

  private cooldowns: Cooldowns = {};

  constructor(mainStore: Main) {
    this.mainStore = mainStore;

    makeAutoObservable(this);

    this.restore();
  }

  startCooldown = (type: OtpType, durationSeconds = DURATION_SECONDS) => {
    const now = this.getNow();
    const expiresAt = this.cooldowns[type];
    if (expiresAt && expiresAt > now) {
      return;
    }

    this.cooldowns[type] = this.getNow() + durationSeconds;

    this.save();
  };

  clear = (type: OtpType) => {
    if (!this.cooldowns[type]) {
      return;
    }

    delete this.cooldowns[type];

    this.save();
  };

  reset = () => {
    this.cooldowns = {};

    localStorage.removeItem(STORAGE_KEY);
  };

  getExpiresAt = (type?: OtpType) => {
    return this.cooldowns[type || ''];
  };

  private restore() {
    const stored = this.load();
    const now = this.getNow();

    const actual: Cooldowns = {};

    for (const type of Object.values(OtpType)) {
      const expiresAt = stored[type];

      if (expiresAt && expiresAt > now) {
        actual[type] = expiresAt;
      }
    }

    this.cooldowns = actual;

    if (Object.keys(actual).length !== Object.keys(stored).length) {
      this.save();
    }
  }

  private load(): Cooldowns {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    } catch {
      return {};
    }
  }

  private save() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.cooldowns));
  }

  private getNow() {
    return Math.floor(Date.now() / 1000);
  }
}
